import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import type { Fixture, Team, Quote, Bookmaker, UserProfile, AnalysisSnapshot } from "@tracker-bet-analystic/domain";
import { DEMO_MODE_KEY, DEMO_USER_ID, setDemoMode } from "@tracker-bet-analystic/domain";
import { DEMO_TEAMS, DEMO_FIXTURES, DEMO_BOOKMAKERS, DEMO_PROFILE } from "../data/demo-data";

interface DemoState {
  isDemo: boolean;
  fixtures: Fixture[];
  teams: Team[];
  quotes: Quote[];
  bookmakers: Bookmaker[];
  profile: UserProfile;
  analyses: AnalysisSnapshot[];
  toggleDemo: () => void;
  addQuote: (quote: Quote) => void;
  addBookmaker: (bookmaker: Omit<Bookmaker, "id" | "userId" | "createdAt" | "updatedAt">) => void;
  updateBookmaker: (id: string, updates: Partial<Bookmaker>) => void;
  deleteBookmaker: (id: string) => void;
  addAnalysis: (analysis: AnalysisSnapshot) => void;
  getAnalysis: (id: string) => AnalysisSnapshot | undefined;
}

const DemoContext = createContext<DemoState | null>(null);

export function DemoProvider({ children }: { children: ReactNode }) {
  const [isDemo, setIsDemoState] = useState(false);
  const [fixtures] = useState<Fixture[]>(DEMO_FIXTURES);
  const [teams] = useState<Team[]>(DEMO_TEAMS);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [bookmakers, setBookmakers] = useState<Bookmaker[]>(DEMO_BOOKMAKERS);
  const [profile] = useState<UserProfile>(DEMO_PROFILE);
  const [analyses, setAnalyses] = useState<AnalysisSnapshot[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(DEMO_MODE_KEY);
    if (stored === "true") {
      setIsDemoState(true);
    }
  }, []);

  const toggleDemo = () => {
    const newValue = !isDemo;
    setIsDemoState(newValue);
    setDemoMode(newValue);
  };

  const addQuote = (quote: Quote) => {
    setQuotes((prev) => [...prev, quote]);
  };

  const addBookmaker = (bookmaker: Omit<Bookmaker, "id" | "userId" | "createdAt" | "updatedAt">) => {
    const newBookmaker: Bookmaker = {
      ...bookmaker,
      id: `bm-${Date.now()}`,
      userId: DEMO_USER_ID,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setBookmakers((prev) => [...prev, newBookmaker]);
  };

  const updateBookmaker = (id: string, updates: Partial<Bookmaker>) => {
    setBookmakers((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...updates, updatedAt: new Date().toISOString() } : b))
    );
  };

  const deleteBookmaker = (id: string) => {
    setBookmakers((prev) => prev.filter((b) => b.id !== id));
  };

  const addAnalysis = (analysis: AnalysisSnapshot) => {
    setAnalyses((prev) => [analysis, ...prev.filter((a) => a.fixture.id !== analysis.fixture.id)]);
  };

  const getAnalysis = (id: string) => {
    return analyses.find((a) => a.fixture.id === id);
  };

  return (
    <DemoContext.Provider
      value={{
        isDemo,
        fixtures,
        teams,
        quotes,
        bookmakers,
        profile,
        analyses,
        toggleDemo,
        addQuote,
        addBookmaker,
        updateBookmaker,
        deleteBookmaker,
        addAnalysis,
        getAnalysis,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error("useDemo must be used within a DemoProvider");
  }
  return context;
}